/**
 * ConsolationPoolFields — the runner-up reward pool.
 *
 * The main treasure list ({@link TreasureListFields}) authors fixed rewards that are placed on
 * specific planets and won outright by whoever clears one first. This pool is the opposite in
 * every way that matters, and the UI has to make that obvious:
 *
 *   - nothing is placed on the map, and nothing is drawn down. Two teams can roll the same
 *     entry on the same planet and both win it;
 *   - an entry is a RANGE with a drop chance, not a quantity. "Cartel packs, 1–3, 50%" means
 *     every runner-up rolls those odds for themselves;
 *   - there is no tier, because tier only exists to bias which round an item is distributed
 *     into and this pool is never distributed.
 *
 * Hence: no tier selector, no quantity field, and the help text leads with "rolled fresh per
 * team" so an admin does not read the numbers as a budget.
 *
 * Purely controlled, mirroring TreasureListFields.
 */

import {
  TRL_TREASURE_KINDS,
  TRL_TREASURE_KIND_LABELS,
  TRL_CONSOLATION_MAX_ITEMS,
  TRL_CONSOLATION_DEFAULT_CHANCE,
  type TrlTreasureConsolationConfig,
  type TrlTreasureConsolationItem,
  type TrlTreasureKind,
} from '@swtorbit/shared-socket-events';
import { TreasureRewardIdentityFields } from './TreasureRewardIdentityFields';

interface ConsolationPoolFieldsProps {
  value: TrlTreasureConsolationConfig;
  onChange: (next: TrlTreasureConsolationConfig) => void;
  disabled?: boolean;
}

/** A fresh entry of the given kind, with bounds an admin will obviously want to change. */
function blankItem(kind: TrlTreasureKind): TrlTreasureConsolationItem {
  const base = { id: crypto.randomUUID(), chance: TRL_CONSOLATION_DEFAULT_CHANCE[kind] };
  switch (kind) {
    case 'credits':
      return { ...base, kind: 'credits', min: 1, max: 1_000_000 };
    case 'augment':
      return { ...base, kind: 'augment', min: 1, max: 1 };
    case 'cartel':
      // No item yet: the row's picker asks for one (an entry left without one is dropped on save).
      return { ...base, kind: 'cartel', min: 1, max: 1 };
    case 'custom':
      return { ...base, kind: 'custom', min: 1, max: 1, title: 'New consolation prize' };
  }
}

/** What the min/max pair counts, per kind — an amount of currency, or a number of copies. */
function boundsLabel(kind: TrlTreasureKind): string {
  switch (kind) {
    case 'credits':
      return 'Credits';
    case 'augment':
      return 'Augments';
    default:
      return 'Copies';
  }
}

export function ConsolationPoolFields({ value, onChange, disabled = false }: ConsolationPoolFieldsProps) {
  const items = value.items;

  // Editing the list stays available while the pool is off, so an admin can author it first
  // and flip the switch when they are happy with it.
  const fieldsDisabled = disabled;

  const patchItem = (id: string, patch: Partial<TrlTreasureConsolationItem>) => {
    onChange({
      ...value,
      // The cast is needed because a partial patch can't prove it keeps the discriminated
      // union intact; every call site patches within one kind.
      items: items.map((it) => (it.id === id ? ({ ...it, ...patch } as TrlTreasureConsolationItem) : it)),
    });
  };

  /** Switching kind rebuilds the row, preserving the chance and the bounds. */
  const changeKind = (id: string, kind: TrlTreasureKind) => {
    const existing = items.find((it) => it.id === id);
    if (!existing) return;
    const replacement = blankItem(kind);
    onChange({
      ...value,
      items: items.map((it) =>
        it.id === id
          ? { ...replacement, id: existing.id, chance: existing.chance, min: existing.min, max: existing.max }
          : it,
      ),
    });
  };

  const removeItem = (id: string) => {
    onChange({ ...value, items: items.filter((it) => it.id !== id) });
  };

  const addItem = () => {
    if (items.length >= TRL_CONSOLATION_MAX_ITEMS) return;
    onChange({ ...value, items: [...items, blankItem('credits')] });
  };

  // How many entries a typical runner-up walks away with. Cheap arithmetic, but it is the
  // number an admin actually wants before they save: it turns a list of odds into "about two
  // prizes each".
  const expectedDrops = items.reduce((sum, it) => sum + it.chance / 100, 0);

  return (
    <div className="d-flex flex-column gap-xs">
      <span className="fs-xs text-uppercase text-slate">Consolation prizes</span>
      <span className="fs-xs text-slate">
        What a team that finishes 2nd or later on a planet can win. Nothing is drawn from a
        fixed quantity — each entry is a chance and a range, rolled fresh for every team, so two
        teams can win the same prize. Only planets that actually hold treasure pay out.
      </span>

      <label className="d-flex align-items-center gap-2xs fs-sm m-0">
        <input
          className="checkbox"
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          disabled={disabled}
        />
        Award consolation prizes to runners-up
      </label>

      {items.length === 0 ? (
        <p className="text-slate fs-sm m-0" style={{ padding: '1rem 0' }}>
          No consolation prizes yet. Add one to get started.
        </p>
      ) : (
        items.map((item) => (
          <div key={item.id} className="border-md border-blue-light rounded-sm p-xs d-flex flex-column gap-2xs">
            <div className="d-flex gap-2xs align-items-center flex-wrap">
              <select
                className="input input--sm"
                value={item.kind}
                onChange={(e) => changeKind(item.id, e.target.value as TrlTreasureKind)}
                disabled={fieldsDisabled}
                aria-label="Reward type"
              >
                {TRL_TREASURE_KINDS.map((k) => (
                  <option key={k} value={k}>{TRL_TREASURE_KIND_LABELS[k]}</option>
                ))}
              </select>

              <label className="fs-xs text-slate d-flex align-items-center gap-2xs m-0">
                Chance
                <input
                  type="number"
                  className="input input--sm"
                  style={{ width: '4.5rem' }}
                  min={0}
                  max={100}
                  value={item.chance}
                  onChange={(e) => patchItem(item.id, { chance: Number(e.target.value) })}
                  disabled={fieldsDisabled}
                  title="Percent chance this prize drops at all, rolled once per team"
                />
                %
              </label>

              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => removeItem(item.id)}
                disabled={fieldsDisabled}
              >
                Remove
              </button>
            </div>

            <div className="d-flex gap-2xs align-items-center flex-wrap">
              <span className="fs-xs text-slate">{boundsLabel(item.kind)}</span>
              <input
                type="number"
                className="input input--sm"
                style={{ width: '8rem' }}
                min={0}
                value={item.min}
                onChange={(e) => patchItem(item.id, { min: Number(e.target.value) })}
                disabled={fieldsDisabled}
                aria-label={`Minimum ${boundsLabel(item.kind).toLowerCase()}`}
                title="The least a team can roll"
              />
              <span className="fs-xs text-slate">to</span>
              <input
                type="number"
                className="input input--sm"
                style={{ width: '8rem' }}
                min={0}
                value={item.max}
                onChange={(e) => patchItem(item.id, { max: Number(e.target.value) })}
                disabled={fieldsDisabled}
                aria-label={`Maximum ${boundsLabel(item.kind).toLowerCase()}`}
                title="The most a team can roll"
              />
            </div>

            <TreasureRewardIdentityFields
              kind={item.kind}
              value={item}
              onChange={(patch) => patchItem(item.id, patch as Partial<TrlTreasureConsolationItem>)}
              disabled={fieldsDisabled}
            />
          </div>
        ))
      )}

      <div className="d-flex align-items-center gap-xs">
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          onClick={addItem}
          disabled={disabled || items.length >= TRL_CONSOLATION_MAX_ITEMS}
        >
          + Add consolation prize
        </button>
        {items.length > 0 && (
          <span className="fs-xs text-slate">
            {items.length} prize{items.length === 1 ? '' : 's'} · about {expectedDrops.toFixed(1)} per
            team
          </span>
        )}
      </div>
    </div>
  );
}
